import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig.js';
import CollapsibleNavbar from './CollapsibleNavbar.jsx';
import ModifyUser from './ModifyUser.jsx';
import ChangePassword from './ChangePassword.jsx';
import ManageCurriculums from './ManageCurriculumsWorkspace.jsx';
import AdminEvaluationCenter from './AdminEvaluationCenter.jsx';
import OrganizationApplicationsReview from './OrganizationApplicationsReview.jsx';
import MyOrganizationPage from './MyOrganizationPage.jsx';
import { normalizeUser } from '../utils/userUtils';
import '../styles/theme.css';
import '../styles/CollapsibleNavbar.css';
import '../styles/SuperAdminDashboard.css';
import LoadingSpinner from './LoadingSpinner';

// Views that already render their own full-width page shell (the `mc-*` /
// universal-white-container layout used by Manage Curricula, or a bespoke one).
// Wrapping them in a `content-box` would nest a white card inside a white card.
const SELF_SHELLED_VIEWS = new Set([
    'change-password',
    'my-account',
    'manage-curriculums',
    'my-organization',
    'organization-applications',
]);

// Super Admin API functions
const SuperAdminAPI = {
    getDashboardData: async () => {
        const response = await axiosInstance.get('/super_admin/dashboard');
        return response.data;
    },
    
    getStatistics: async () => {
        const response = await axiosInstance.get('/super_admin/statistics');
        return response.data;
    },
    
    getLicenses: async () => {
        const response = await axiosInstance.get('/super_admin/licenses');
        return response.data;
    },
    
    createLicense: async (licenseData) => {
        const response = await axiosInstance.post('/super_admin/license/', licenseData);
        return response.data;
    },
    
    updateLicense: async (licenseId, updateData) => {
        const response = await axiosInstance.put(`/super_admin/licenses/${licenseId}`, updateData);
        return response.data;
    },
    
    expireLicense: async (licenseId) => {
        const response = await axiosInstance.delete(`/super_admin/licenses/${licenseId}`);
        return response.data;
    },
    
    extendLicense: async (licenseId, extensionDays) => {
        const response = await axiosInstance.post(`/super_admin/licenses/${licenseId}/extend`, {
            extension_days: extensionDays
        });
        return response.data;
    }
};

// Helper function to get current user ID
const getCurrentUserId = () => {
    try {
        const token = localStorage.getItem('access_token');
        if (!token) return null;
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.user_id || payload.sub || payload.id;
    } catch (error) {
        console.error('Error getting user ID:', error);
        return null;
    }
};

const SuperAdminDashboard = () => {
    const navigate = useNavigate();
    const { view } = useParams();

    // Initialize currentView from URL parameter or default to "dashboard"
    const currentView = view || "dashboard";

    // Update URL when view changes
    const handleViewChange = (newView) => {
        if (newView === "dashboard") {
            navigate("/SuperAdminDashboard", { replace: true });
        } else {
            navigate(`/SuperAdminDashboard/${newView}`, { replace: true });
        }
    };
    const [dashboardData, setDashboardData] = useState(null);
    const [statistics, setStatistics] = useState(null);
    const [licenses, setLicenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [selectedLicense, setSelectedLicense] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showExtendModal, setShowExtendModal] = useState(false);
    const [showExpireModal, setShowExpireModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [extendDays, setExtendDays] = useState(30);
    const [actionLoading, setActionLoading] = useState(false);

    const userId = getCurrentUserId();

    // Enhanced date formatting
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Invalid Date';
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            console.error('Date formatting error:', error);
            return 'Invalid Date';
        }
    };

    // Load dashboard data
    const loadDashboardData = useCallback(async () => {
        try {
            setLoading(true);
            const [dashData, licensesData, statsData] = await Promise.all([
                SuperAdminAPI.getDashboardData(),
                SuperAdminAPI.getLicenses(),
                SuperAdminAPI.getStatistics()
            ]);
            if (dashData?.super_admin_data) {
                dashData.super_admin_data = normalizeUser(dashData.super_admin_data);
            }
            setDashboardData(dashData);
            setLicenses(licensesData.licenses || []);
            setStatistics(statsData);
            setError("");
        } catch (error) {
            console.error('Failed to load dashboard data:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadDashboardData();
    }, [loadDashboardData]);

    // License operations
    const handleCreateLicense = async (licenseData) => {
        try {
            await SuperAdminAPI.createLicense(licenseData);
            loadDashboardData();
            setShowCreateModal(false);
            alert('License created successfully!');
        } catch (error) {
            alert('Failed to create license: ' + error.message);
        }
    };

    const handleUpdateLicense = async (licenseId, updateData) => {
        try {
            await SuperAdminAPI.updateLicense(licenseId, updateData);
            loadDashboardData();
            setShowEditModal(false);
            setSelectedLicense(null);
            alert('License updated successfully!');
        } catch (error) {
            alert('Failed to update license: ' + error.message);
        }
    };

    const handleDeleteLicense = async (licenseId) => {
        if (!window.confirm('Are you sure you want to delete this license? This action cannot be undone.')) {
            return;
        }
        try {
            await SuperAdminAPI.deleteLicense(licenseId);
            loadDashboardData();
            alert('License deleted successfully!');
        } catch (error) {
            alert('Failed to delete license: ' + error.message);
        }
    };

    // Enhanced license operations
    const handleExpireLicense = async (licenseId) => {
        try {
            setActionLoading(true);
            await SuperAdminAPI.expireLicense(licenseId);
            loadDashboardData();
            setShowExpireModal(false);
            setSelectedLicense(null);
            alert('License has been expired successfully!');
        } catch (error) {
            alert('Failed to expire license: ' + error.message);
        } finally {
            setActionLoading(false);
        }
    };

    const handleExtendLicense = async (licenseId, days) => {
        try {
            setActionLoading(true);
            const result = await SuperAdminAPI.extendLicense(licenseId, days);
            loadDashboardData();
            setShowExtendModal(false);
            setSelectedLicense(null);
            alert(`License extended successfully! New end date: ${new Date(result.new_end_date).toLocaleDateString()}`);
        } catch (error) {
            alert('Failed to extend license: ' + error.message);
        } finally {
            setActionLoading(false);
        }
    };

    // Get status styling
    const getStatusBadge = (status) => {
        const baseClass = "status-badge";
        switch (status?.toLowerCase()) {
            case 'active':
                return `${baseClass} status-active`;
            case 'expired':
                return `${baseClass} status-expired`;
            case 'trial':
                return `${baseClass} status-trial`;
            default:
                return `${baseClass} status-inactive`;
        }
    };

    // Filter licenses
    const filteredLicenses = licenses.filter(license => {
        const matchesSearch = license.organizationName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             license.licenseKey?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filterStatus === 'all' || license.licenseStatus === filterStatus;
        return matchesSearch && matchesFilter;
    });

    // Calculate analytics with enhanced statistics
    const calculateAnalytics = () => {
        if (!dashboardData || !licenses.length || !statistics) return null;

        const totalRevenue = licenses.reduce((sum, license) => sum + (license.Cost || 0), 0);
        const totalPaid = licenses.reduce((sum, license) => sum + (license.Paid || 0), 0);
        const paymentPercentage = totalRevenue > 0 ? ((totalPaid / totalRevenue) * 100).toFixed(1) : 0;

        const totalStudentCapacity = licenses.reduce((sum, license) => sum + (license.maxStudents || 0), 0);
        const totalPreceptorCapacity = licenses.reduce((sum, license) => sum + (license.maxPreceptors || 0), 0);
        const totalAdminCapacity = licenses.reduce((sum, license) => sum + (license.maxStandaloneAdmins || 0), 0);

        const planDistribution = licenses.reduce((acc, license) => {
            const plan = license.subscriptionPlan || 'unknown';
            acc[plan] = (acc[plan] || 0) + 1;
            return acc;
        }, {});

        const expiringMaybe = licenses.filter(license => {
            const endDate = new Date(license.licenseEndDate);
            const now = new Date();
            const daysUntilExpiry = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
            return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
        }).length;

        // Enhanced metrics with statistics integration
        const utilizationRate = statistics.user_statistics ? 
            ((statistics.user_statistics.total_users || 0) / totalStudentCapacity * 100).toFixed(1) : 0;

        return {
            totalRevenue,
            totalPaid,
            paymentPercentage,
            totalStudentCapacity,
            totalPreceptorCapacity,
            totalAdminCapacity,
            planDistribution,
            expiringMaybe,
            utilizationRate,
            userStats: statistics.user_statistics,
            licenseStats: statistics.license_statistics
        };
    };

    const analytics = calculateAnalytics();

    // Render Dashboard Home
    const renderDashboard = () => {
        if (!dashboardData) return <LoadingSpinner text="Loading dashboard data..." />;

        return (
            <div className="modern-dashboard">
                <div className="dashboard-header">
                    <h1 className="dashboard-title">Super Admin Dashboard</h1>
                    <p className="dashboard-subtitle">
                        Welcome, {dashboardData.super_admin_data?.firstname} {dashboardData.super_admin_data?.lastname}
                    </p>
                </div>

                {/* Enhanced Key Metrics Grid with Real-time Statistics */}
                <div className="metrics-grid">
                    <div className="metric-card primary">
                        <div className="metric-icon">🏢</div>
                        <div className="metric-content">
                            <h3 className="metric-number">{analytics?.licenseStats?.total_licenses || dashboardData.total_licenses}</h3>
                            <p className="metric-label">Total Licenses</p>
                            <div className="metric-trend">
                                <span className="trend-indicator positive">↑</span>
                                <span className="trend-text">+{analytics?.licenseStats?.trial_licenses || 0} trials</span>
                            </div>
                        </div>
                    </div>

                    <div className="metric-card success">
                        <div className="metric-icon">✅</div>
                        <div className="metric-content">
                            <h3 className="metric-number">{analytics?.licenseStats?.active_licenses || dashboardData.active_licenses}</h3>
                            <p className="metric-label">Active Licenses</p>
                            <div className="metric-trend">
                                <span className="trend-indicator neutral">→</span>
                                <span className="trend-text">{((analytics?.licenseStats?.active_licenses || dashboardData.active_licenses) / (analytics?.licenseStats?.total_licenses || dashboardData.total_licenses) * 100).toFixed(1)}% active</span>
                            </div>
                        </div>
                    </div>

                    <div className="metric-card warning">
                        <div className="metric-icon">⚠️</div>
                        <div className="metric-content">
                            <h3 className="metric-number">{analytics?.licenseStats?.expired_licenses || dashboardData.expired_licenses}</h3>
                            <p className="metric-label">Expired Licenses</p>
                            <div className="metric-trend">
                                <span className="trend-indicator warning">!</span>
                                <span className="trend-text">{analytics?.expiringMaybe || 0} expiring soon</span>
                            </div>
                        </div>
                    </div>

                    <div className="metric-card info">
                        <div className="metric-icon">👥</div>
                        <div className="metric-content">
                            <h3 className="metric-number">{analytics?.userStats?.total_users || 0}</h3>
                            <p className="metric-label">Total Users</p>
                            <div className="metric-trend">
                                <span className="trend-indicator positive">↗</span>
                                <span className="trend-text">{analytics?.utilizationRate || 0}% utilization</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Enhanced User Statistics Overview */}
                {analytics?.userStats && (
                    <div className="analytics-overview">
                        <div className="dashboard-card user-stats-card">
                            <div className="card-header">
                                <h2 className="card-title">🎯 Live User Statistics</h2>
                                <div className="stats-refresh">
                                    <span className="refresh-indicator">🔄</span>
                                    <span className="refresh-text">Real-time</span>
                                </div>
                            </div>
                            <div className="card-content">
                                <div className="user-stats-grid">
                                    <div className="user-stat-item students">
                                        <div className="stat-icon">👨‍🎓</div>
                                        <div className="stat-details">
                                            <span className="stat-number">{analytics.userStats.total_students}</span>
                                            <span className="stat-label">Students</span>
                                            <div className="stat-bar">
                                                <div className="stat-fill" style={{ width: `${(analytics.userStats.total_students / analytics.totalStudentCapacity) * 100}%` }}></div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="user-stat-item preceptors">
                                        <div className="stat-icon">👨‍⚕️</div>
                                        <div className="stat-details">
                                            <span className="stat-number">{analytics.userStats.total_preceptors}</span>
                                            <span className="stat-label">Preceptors</span>
                                            <div className="stat-bar">
                                                <div className="stat-fill" style={{ width: `${(analytics.userStats.total_preceptors / analytics.totalPreceptorCapacity) * 100}%` }}></div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="user-stat-item admins">
                                        <div className="stat-icon">👨‍💼</div>
                                        <div className="stat-details">
                                            <span className="stat-number">{analytics.userStats.total_admins}</span>
                                            <span className="stat-label">Admins</span>
                                            <div className="stat-bar">
                                                <div className="stat-fill" style={{ width: `${Math.min((analytics.userStats.total_admins / analytics.totalAdminCapacity) * 100, 100)}%` }}></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Analytics Overview */}
                {analytics && (
                    <div className="analytics-overview">
                        <div className="dashboard-card">
                            <div className="card-header">
                                <h2 className="card-title">Financial Overview</h2>
                            </div>
                            <div className="card-content">
                                <div className="financial-metrics">
                                    <div className="financial-item">
                                        <span className="financial-label">Total Revenue</span>
                                        <span className="financial-value">${analytics.totalRevenue.toLocaleString()}</span>
                                    </div>
                                    <div className="financial-item">
                                        <span className="financial-label">Amount Paid</span>
                                        <span className="financial-value">${analytics.totalPaid.toLocaleString()}</span>
                                    </div>
                                    <div className="financial-item">
                                        <span className="financial-label">Payment Percentage</span>
                                        <span className="financial-value highlight">{analytics.paymentPercentage}%</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="dashboard-card">
                            <div className="card-header">
                                <h2 className="card-title">Capacity Overview</h2>
                            </div>
                            <div className="card-content">
                                <div className="capacity-metrics">
                                    <div className="capacity-item">
                                        <span className="capacity-icon">👨‍🎓</span>
                                        <div className="capacity-details">
                                            <span className="capacity-number">{analytics.totalStudentCapacity.toLocaleString()}</span>
                                            <span className="capacity-label">Total Student Capacity</span>
                                        </div>
                                    </div>
                                    <div className="capacity-item">
                                        <span className="capacity-icon">👨‍⚕️</span>
                                        <div className="capacity-details">
                                            <span className="capacity-number">{analytics.totalPreceptorCapacity.toLocaleString()}</span>
                                            <span className="capacity-label">Total Preceptor Capacity</span>
                                        </div>
                                    </div>
                                    <div className="capacity-item">
                                        <span className="capacity-icon">👨‍💼</span>
                                        <div className="capacity-details">
                                            <span className="capacity-number">{analytics.totalAdminCapacity.toLocaleString()}</span>
                                            <span className="capacity-label">Total Admin Capacity</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* License Overview */}
                <div className="dashboard-card">
                    <div className="card-header">
                        <h2 className="card-title">License Overview</h2>
                        <button 
                            className="btn btn-primary"
                            onClick={() => setShowCreateModal(true)}
                        >
                            + Create License
                        </button>
                    </div>
                    <div className="card-content">
                        <div className="license-grid">
                            {filteredLicenses.slice(0, 6).map(license => (
                                <div 
                                    key={license._id} 
                                    className="license-card"
                                    onClick={() => setSelectedLicense(license)}
                                >
                                    <div className="license-header">
                                        <h3 className="license-org">{license.organizationName}</h3>
                                        <span className={getStatusBadge(license.licenseStatus)}>
                                            {license.licenseStatus}
                                        </span>
                                    </div>
                                    <div className="license-details">
                                        <p className="license-plan">{license.subscriptionPlan} Plan</p>
                                        <p className="license-dates">
                                            {formatDate(license.licenseStartDate)} - {formatDate(license.licenseEndDate)}
                                        </p>
                                        <div className="license-capacity">
                                            <span>👨‍🎓 {license.maxStudents}</span>
                                            <span>👨‍⚕️ {license.maxPreceptors}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {filteredLicenses.length > 6 && (
                            <div className="view-all-container">
                                <button 
                                    className="btn btn-outline"
                                    onClick={() => handleViewChange('licenses')}
                                >
                                    View All {filteredLicenses.length} Licenses
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Render Analytics Page
    const renderAnalytics = () => {
        if (!analytics) return <LoadingSpinner text="Loading analytics..." />;

        return (
            <div className="analytics-dashboard">
                <div className="dashboard-header">
                    <h1 className="dashboard-title">Analytics Dashboard</h1>
                    <p className="dashboard-subtitle">Comprehensive system analytics and insights</p>
                </div>

                {/* Financial Analytics */}
                <div className="analytics-section">
                    <h2 className="section-title">Financial Analytics</h2>
                    <div className="analytics-grid">
                        <div className="analytics-card">
                            <div className="card-header">
                                <h3 className="card-title">Revenue Overview</h3>
                            </div>
                            <div className="card-content">
                                <div className="revenue-chart">
                                    <div className="revenue-item">
                                        <span className="revenue-label">Total Revenue</span>
                                        <span className="revenue-amount">${analytics.totalRevenue.toLocaleString()}</span>
                                    </div>
                                    <div className="revenue-item">
                                        <span className="revenue-label">Amount Collected</span>
                                        <span className="revenue-amount">${analytics.totalPaid.toLocaleString()}</span>
                                    </div>
                                    <div className="revenue-item">
                                        <span className="revenue-label">Outstanding</span>
                                        <span className="revenue-amount">${(analytics.totalRevenue - analytics.totalPaid).toLocaleString()}</span>
                                    </div>
                                    <div className="payment-percentage">
                                        <div className="percentage-label">Payment Collection Rate</div>
                                        <div className="percentage-bar">
                                            <div 
                                                className="percentage-fill"
                                                style={{ width: `${analytics.paymentPercentage}%` }}
                                            ></div>
                                        </div>
                                        <div className="percentage-text">{analytics.paymentPercentage}%</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="analytics-card">
                            <div className="card-header">
                                <h3 className="card-title">Subscription Plans</h3>
                            </div>
                            <div className="card-content">
                                <div className="plan-distribution">
                                    {Object.entries(analytics.planDistribution).map(([plan, count]) => (
                                        <div key={plan} className="plan-item">
                                            <span className="plan-name">{plan.charAt(0).toUpperCase() + plan.slice(1)}</span>
                                            <span className="plan-count">{count} licenses</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Capacity Analytics */}
                <div className="analytics-section">
                    <h2 className="section-title">Capacity Analytics</h2>
                    <div className="analytics-grid">
                        <div className="analytics-card">
                            <div className="card-header">
                                <h3 className="card-title">System Capacity</h3>
                            </div>
                            <div className="card-content">
                                <div className="capacity-overview">
                                    <div className="capacity-stat">
                                        <div className="capacity-icon">👨‍🎓</div>
                                        <div className="capacity-info">
                                            <span className="capacity-number">{analytics.totalStudentCapacity.toLocaleString()}</span>
                                            <span className="capacity-label">Total Student Capacity</span>
                                        </div>
                                    </div>
                                    <div className="capacity-stat">
                                        <div className="capacity-icon">👨‍⚕️</div>
                                        <div className="capacity-info">
                                            <span className="capacity-number">{analytics.totalPreceptorCapacity.toLocaleString()}</span>
                                            <span className="capacity-label">Total Preceptor Capacity</span>
                                        </div>
                                    </div>
                                    <div className="capacity-stat">
                                        <div className="capacity-icon">👨‍💼</div>
                                        <div className="capacity-info">
                                            <span className="capacity-number">{analytics.totalAdminCapacity.toLocaleString()}</span>
                                            <span className="capacity-label">Total Admin Capacity</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="analytics-card">
                            <div className="card-header">
                                <h3 className="card-title">License Status</h3>
                            </div>
                            <div className="card-content">
                                <div className="status-overview">
                                    <div className="status-item">
                                        <span className="status-indicator active"></span>
                                        <span className="status-label">Active Licenses</span>
                                        <span className="status-count">{dashboardData.active_licenses}</span>
                                    </div>
                                    <div className="status-item">
                                        <span className="status-indicator expired"></span>
                                        <span className="status-label">Expired Licenses</span>
                                        <span className="status-count">{dashboardData.expired_licenses}</span>
                                    </div>
                                    <div className="status-item">
                                        <span className="status-indicator trial"></span>
                                        <span className="status-label">Trial Licenses</span>
                                        <span className="status-count">{dashboardData.trial_licenses}</span>
                                    </div>
                                    <div className="status-item">
                                        <span className="status-indicator warning"></span>
                                        <span className="status-label">Expiring Soon</span>
                                        <span className="status-count">{analytics.expiringMaybe}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Render Licenses Page
    const renderLicenses = () => {
        return (
            <div className="licenses-dashboard">
                <div className="dashboard-header">
                    <h1 className="dashboard-title">License Management</h1>
                    <p className="dashboard-subtitle">Manage all organization licenses</p>
                </div>

                {/* Enhanced Controls with Modern Dark-Blue Theme */}
                <div className="licenses-controls-modern">
                    <div className="controls-section-modern">
                        <div className="search-section-modern">
                            <div className="search-wrapper-modern">
                                <div className="search-icon-modern">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                                        <path d="m21 21-4.35-4.35" stroke="currentColor" strokeWidth="2"/>
                                    </svg>
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search organizations, license keys, contacts..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="search-input-modern"
                                />
                                {searchTerm && (
                                    <button 
                                        className="clear-search-modern"
                                        onClick={() => setSearchTerm('')}
                                        title="Clear search"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2"/>
                                            <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2"/>
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>
                        
                        <div className="filters-section-modern">
                            <div className="filter-wrapper-modern">
                                <label className="filter-label-modern">Status Filter</label>
                                <div className="select-wrapper-modern">
                                    <select
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                        className="filter-select-modern"
                                    >
                                        <option value="all">🌐 All Status ({licenses.length})</option>
                                        <option value="active">✅ Active ({licenses.filter(l => l.licenseStatus === 'active').length})</option>
                                        <option value="expired">⚠️ Expired ({licenses.filter(l => l.licenseStatus === 'expired').length})</option>
                                        <option value="trial">🆓 Trial ({licenses.filter(l => l.licenseStatus === 'trial').length})</option>
                                    </select>
                                    <div className="select-arrow-modern">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="results-counter-modern">
                                <div className="counter-icon-modern">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M9 11H15M9 15H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L19.7071 9.70711C19.8946 9.89464 20 10.149 20 10.4142V19C20 20.1046 19.1046 21 18 21H17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </div>
                                <div className="counter-content-modern">
                                    <span className="counter-number-modern">{filteredLicenses.length}</span>
                                    <span className="counter-label-modern">Results</span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="actions-section-modern">
                            <button 
                                className="create-license-btn-modern"
                                onClick={() => setShowCreateModal(true)}
                                title="Create new organization license"
                            >
                                <div className="btn-icon-modern">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                                        <path d="M12 8v8M8 12h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                </div>
                                <span className="btn-text-modern">Create License</span>
                            </button>
                        </div>
                    </div>
                    
                    {/* Modern Filter Summary Bar */}
                    {(searchTerm || filterStatus !== 'all') && (
                        <div className="filter-summary-modern">
                            <div className="summary-content-modern">
                                <div className="summary-icon-modern">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M3 6h18l-2 13H5L3 6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M8 10v4M12 10v4M16 10v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                </div>
                                <span className="summary-text-modern">
                                    Showing {filteredLicenses.length} of {licenses.length} licenses
                                    {searchTerm && <span className="filter-tag-modern">"{searchTerm}"</span>}
                                    {filterStatus !== 'all' && <span className="filter-tag-modern">{filterStatus}</span>}
                                </span>
                            </div>
                            <button
                                className="clear-filters-modern"
                                onClick={() => {
                                    setSearchTerm('');
                                    setFilterStatus('all');
                                }}
                                title="Clear all filters"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2"/>
                                    <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2"/>
                                </svg>
                                Clear All
                            </button>
                        </div>
                    )}
                </div>

                {/* Licenses Grid */}
                <div className="licenses-grid">
                    {filteredLicenses.map(license => (
                        <div key={license._id} className="modern-license-card">
                            <div className="license-card-header modern">
                                <div className="license-title-section">
                                    <h3 className="license-org-name">{license.organizationName}</h3>
                                    <div className="license-key-badge">{license.licenseKey}</div>
                                </div>
                                <span className={getStatusBadge(license.licenseStatus)}>
                                    {license.licenseStatus}
                                </span>
                            </div>
                            
                            <div className="license-card-body modern">
                                <div className="license-info-grid">
                                    <div className="info-section">
                                        <div className="info-item">
                                            <span className="info-icon">📋</span>
                                            <div className="info-content">
                                                <span className="info-label">Plan</span>
                                                <span className="info-value">{license.subscriptionPlan}</span>
                                            </div>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-icon">📧</span>
                                            <div className="info-content">
                                                <span className="info-label">Contact</span>
                                                <span className="info-value">{license.contactEmail}</span>
                                            </div>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-icon">📍</span>
                                            <div className="info-content">
                                                <span className="info-label">Location</span>
                                                <span className="info-value">{license.address?.city}, {license.address?.country}</span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="dates-section">
                                        <div className="date-item">
                                            <span className="date-label">Start Date</span>
                                            <span className="date-value">{formatDate(license.licenseStartDate)}</span>
                                        </div>
                                        <div className="date-item">
                                            <span className="date-label">End Date</span>
                                            <span className="date-value">{formatDate(license.licenseEndDate)}</span>
                                        </div>
                                    </div>
                                    
                                    <div className="capacity-section">
                                        <div className="capacity-title">User Capacity</div>
                                        <div className="capacity-grid">
                                            <div className="capacity-item modern">
                                                <span className="capacity-icon">👨‍🎓</span>
                                                <span className="capacity-value">{license.maxStudents}</span>
                                                <span className="capacity-label">Students</span>
                                            </div>
                                            <div className="capacity-item modern">
                                                <span className="capacity-icon">👨‍⚕️</span>
                                                <span className="capacity-value">{license.maxPreceptors}</span>
                                                <span className="capacity-label">Preceptors</span>
                                            </div>
                                            <div className="capacity-item modern">
                                                <span className="capacity-icon">👨‍💼</span>
                                                <span className="capacity-value">{license.maxStandaloneAdmins}</span>
                                                <span className="capacity-label">Admins</span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="financial-section">
                                        <div className="financial-title">Financial Overview</div>
                                        <div className="financial-metrics">
                                            <div className="financial-item">
                                                <span className="financial-label">Total Cost</span>
                                                <span className="financial-value">${license.Cost || 0}</span>
                                            </div>
                                            <div className="financial-item">
                                                <span className="financial-label">Amount Paid</span>
                                                <span className="financial-value">${license.Paid || 0}</span>
                                            </div>
                                            {license.Cost > 0 && (
                                                <div className="financial-item">
                                                    <span className="financial-label">Payment Status</span>
                                                    <div className="payment-progress">
                                                        <div className="payment-bar">
                                                            <div 
                                                                className="payment-fill"
                                                                style={{ width: `${((license.Paid / license.Cost) * 100)}%` }}
                                                            ></div>
                                                        </div>
                                                        <span className="payment-percentage">{((license.Paid / license.Cost) * 100).toFixed(1)}%</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="license-card-actions modern">
                                <button 
                                    className="action-btn edit-btn"
                                    onClick={() => {
                                        setSelectedLicense(license);
                                        setShowEditModal(true);
                                    }}
                                    title="Edit license details"
                                >
                                    <span className="btn-icon">✏️</span>
                                    <span className="btn-text">Edit</span>
                                </button>
                                
                                {license.licenseStatus === 'active' && (
                                    <button 
                                        className="action-btn extend-btn"
                                        onClick={() => {
                                            setSelectedLicense(license);
                                            setShowExtendModal(true);
                                        }}
                                        title="Extend license duration"
                                    >
                                        <span className="btn-icon">📅</span>
                                        <span className="btn-text">Extend</span>
                                    </button>
                                )}
                                
                                <button 
                                    className="action-btn expire-btn"
                                    onClick={() => {
                                        setSelectedLicense(license);
                                        setShowExpireModal(true);
                                    }}
                                    title={license.licenseStatus === 'active' ? 'Expire this license (make it inactive)' : 'License is already expired'}
                                    disabled={license.licenseStatus === 'expired'}
                                >
                                    <span className="btn-icon">⏰</span>
                                    <span className="btn-text">{license.licenseStatus === 'expired' ? 'Expired' : 'Expire'}</span>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // License Modal Components
    const LicenseModal = ({ isEdit = false, license = null, onSubmit, onClose }) => {
        const [formData, setFormData] = useState({
            organizationName: license?.organizationName || '',
            licenseKey: license?.licenseKey || '',
            subscriptionPlan: license?.subscriptionPlan || 'trial',
            maxStudents: license?.maxStudents || 100,
            maxPreceptors: license?.maxPreceptors || 50,
            maxStandaloneAdmins: license?.maxStandaloneAdmins || 5,
            maxStudentsPerCurriculum: license?.maxStudentsPerCurriculum || 50,
            maxPreceptorsPerSpecialty: license?.maxPreceptorsPerSpecialty || 25,
            licenseStartDate: license?.licenseStartDate?.split('T')[0] || new Date().toISOString().split('T')[0],
            licenseEndDate: license?.licenseEndDate?.split('T')[0] || new Date(Date.now() + 365*24*60*60*1000).toISOString().split('T')[0],
            contactEmail: license?.contactEmail || '',
            address: {
                country: license?.address?.country || '',
                city: license?.address?.city || '',
                street: license?.address?.street || '',
                block: license?.address?.block || ''
            }
        });

        // Keyboard escape support
        useEffect(() => {
            const handleEscapeKey = (event) => {
                if (event.key === 'Escape') {
                    onClose();
                }
            };

            document.addEventListener('keydown', handleEscapeKey);
            return () => {
                document.removeEventListener('keydown', handleEscapeKey);
            };
        }, [onClose]);

        // Prevent body scroll when modal is open
        useEffect(() => {
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = 'unset';
            };
        }, []);

        const handleSubmit = (e) => {
            e.preventDefault();
            const submitData = {
                ...formData,
                licenseStartDate: new Date(formData.licenseStartDate).toISOString(),
                licenseEndDate: new Date(formData.licenseEndDate).toISOString()
            };
            
            if (isEdit) {
                onSubmit(license._id, submitData);
            } else {
                onSubmit(submitData);
            }
        };

        return (
            <div 
                className="modal-overlay" 
                onClick={onClose}
                style={{ cursor: 'pointer' }}
            >
                <div 
                    className="modal-content license-modal" 
                    onClick={(e) => e.stopPropagation()}
                    style={{ cursor: 'default' }}
                >
                    <div className="modal-header">
                        <h2>{isEdit ? 'Edit License' : 'Create New License'}</h2>
                        <button 
                            className="modal-close" 
                            onClick={onClose}
                            type="button"
                            aria-label="Close modal"
                        >
                            ×
                        </button>
                    </div>
                    
                    <form onSubmit={handleSubmit} className="license-form">
                        <div className="form-grid">
                            <div className="form-group">
                                <label>Organization Name *</label>
                                <input
                                    type="text"
                                    value={formData.organizationName}
                                    onChange={(e) => setFormData({...formData, organizationName: e.target.value})}
                                    required
                                />
                            </div>
                            
                            <div className="form-group">
                                <label>License Key *</label>
                                <input
                                    type="text"
                                    value={formData.licenseKey}
                                    onChange={(e) => setFormData({...formData, licenseKey: e.target.value})}
                                    required
                                />
                            </div>
                            
                            <div className="form-group">
                                <label>Subscription Plan</label>
                                <select
                                    value={formData.subscriptionPlan}
                                    onChange={(e) => setFormData({...formData, subscriptionPlan: e.target.value})}
                                >
                                    <option value="trial">Trial</option>
                                    <option value="basic">Basic</option>
                                    <option value="premium">Premium</option>
                                    <option value="enterprise">Enterprise</option>
                                </select>
                            </div>
                            
                            <div className="form-group">
                                <label>Contact Email *</label>
                                <input
                                    type="email"
                                    value={formData.contactEmail}
                                    onChange={(e) => setFormData({...formData, contactEmail: e.target.value})}
                                    required
                                />
                            </div>
                            
                            <div className="form-group">
                                <label>Max Students</label>
                                <input
                                    type="number"
                                    value={formData.maxStudents}
                                    onChange={(e) => setFormData({...formData, maxStudents: parseInt(e.target.value)})}
                                />
                            </div>
                            
                            <div className="form-group">
                                <label>Max Preceptors</label>
                                <input
                                    type="number"
                                    value={formData.maxPreceptors}
                                    onChange={(e) => setFormData({...formData, maxPreceptors: parseInt(e.target.value)})}
                                />
                            </div>
                            
                            <div className="form-group">
                                <label>Start Date</label>
                                <input
                                    type="date"
                                    value={formData.licenseStartDate}
                                    onChange={(e) => setFormData({...formData, licenseStartDate: e.target.value})}
                                />
                            </div>
                            
                            <div className="form-group">
                                <label>End Date</label>
                                <input
                                    type="date"
                                    value={formData.licenseEndDate}
                                    onChange={(e) => setFormData({...formData, licenseEndDate: e.target.value})}
                                />
                            </div>
                        </div>
                        
                        <div className="address-section">
                            <h3>Address Information</h3>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Country</label>
                                    <input
                                        type="text"
                                        value={formData.address.country}
                                        onChange={(e) => setFormData({
                                            ...formData, 
                                            address: {...formData.address, country: e.target.value}
                                        })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>City</label>
                                    <input
                                        type="text"
                                        value={formData.address.city}
                                        onChange={(e) => setFormData({
                                            ...formData, 
                                            address: {...formData.address, city: e.target.value}
                                        })}
                                    />
                                </div>
                            </div>
                        </div>
                        
                        <div className="modal-actions">
                            <button type="button" className="btn btn-outline" onClick={onClose}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary">
                                {isEdit ? 'Update License' : 'Create License'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    // License Detail Modal
    const LicenseDetailModal = ({ license, onClose, onEdit, onDelete }) => {
        if (!license) return null;

        // Keyboard escape support
        useEffect(() => {
            const handleEscapeKey = (event) => {
                if (event.key === 'Escape') {
                    onClose();
                }
            };

            document.addEventListener('keydown', handleEscapeKey);
            return () => {
                document.removeEventListener('keydown', handleEscapeKey);
            };
        }, [onClose]);

        // Prevent body scroll when modal is open
        useEffect(() => {
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = 'unset';
            };
        }, []);

        return (
            <div 
                className="modal-overlay" 
                onClick={onClose}
                style={{ cursor: 'pointer' }}
            >
                <div 
                    className="modal-content license-detail-modal" 
                    onClick={(e) => e.stopPropagation()}
                    style={{ cursor: 'default' }}
                >
                    <div className="modal-header">
                        <h2>{license.organizationName}</h2>
                        <button 
                            className="modal-close" 
                            onClick={onClose}
                            type="button"
                            aria-label="Close modal"
                        >
                            ×
                        </button>
                    </div>
                    
                    <div className="license-detail-content">
                        <div className="detail-section">
                            <h3>Basic Information</h3>
                            <div className="detail-grid">
                                <div className="detail-item">
                                    <span className="detail-label">License Key:</span>
                                    <span className="detail-value">{license.licenseKey}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Status:</span>
                                    <span className={getStatusBadge(license.licenseStatus)}>
                                        {license.licenseStatus}
                                    </span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Plan:</span>
                                    <span className="detail-value">{license.subscriptionPlan}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Contact:</span>
                                    <span className="detail-value">{license.contactEmail}</span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="detail-section">
                            <h3>Capacity Limits</h3>
                            <div className="capacity-grid">
                                <div className="capacity-detail">
                                    <span className="capacity-icon">👨‍🎓</span>
                                    <span className="capacity-number">{license.maxStudents}</span>
                                    <span className="capacity-label">Students</span>
                                </div>
                                <div className="capacity-detail">
                                    <span className="capacity-icon">👨‍⚕️</span>
                                    <span className="capacity-number">{license.maxPreceptors}</span>
                                    <span className="capacity-label">Preceptors</span>
                                </div>
                                <div className="capacity-detail">
                                    <span className="capacity-icon">👨‍💼</span>
                                    <span className="capacity-number">{license.maxStandaloneAdmins}</span>
                                    <span className="capacity-label">Admins</span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="detail-section">
                            <h3>License Period</h3>
                            <div className="date-range">
                                <div className="date-item">
                                    <span className="date-label">Start Date:</span>
                                    <span className="date-value">{formatDate(license.licenseStartDate)}</span>
                                </div>
                                <div className="date-item">
                                    <span className="date-label">End Date:</span>
                                    <span className="date-value">{formatDate(license.licenseEndDate)}</span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="detail-section">
                            <h3>Financial Information</h3>
                            <div className="financial-grid">
                                <div className="financial-detail">
                                    <span className="financial-label">Total Cost:</span>
                                    <span className="financial-value">${license.Cost || 0}</span>
                                </div>
                                <div className="financial-detail">
                                    <span className="financial-label">Amount Paid:</span>
                                    <span className="financial-value">${license.Paid || 0}</span>
                                </div>
                                {license.Cost > 0 && (
                                    <div className="financial-detail">
                                        <span className="financial-label">Payment %:</span>
                                        <span className="financial-value highlight">
                                            {((license.Paid / license.Cost) * 100).toFixed(1)}%
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    <div className="modal-actions">
                        <button className="btn btn-outline" onClick={onClose}>
                            Close
                        </button>
                        <button className="btn btn-primary" onClick={() => onEdit(license)}>
                            <span className="btn-icon">✏️</span>
                            Edit License
                        </button>
                        {license.licenseStatus === 'active' && (
                            <button 
                                className="btn btn-success" 
                                onClick={() => {
                                    setSelectedLicense(license);
                                    setShowExtendModal(true);
                                    onClose();
                                }}
                            >
                                <span className="btn-icon">📅</span>
                                Extend License
                            </button>
                        )}
                        <button 
                            className="btn btn-warning" 
                            onClick={() => {
                                setSelectedLicense(license);
                                setShowExpireModal(true);
                                onClose();
                            }}
                            disabled={license.licenseStatus === 'expired'}
                        >
                            <span className="btn-icon">⏰</span>
                            {license.licenseStatus === 'expired' ? 'Already Expired' : 'Expire License'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Main render function
    const renderContent = () => {
        switch (currentView) {
            case "analytics":
                return renderAnalytics();
            case "licenses":
                return renderLicenses();
            case "organization-applications":
                return <OrganizationApplicationsReview />;
            case "my-organization":
                return <MyOrganizationPage canEdit={true} />;
            case "manage-curriculums":
                return (
                    <div className="curriculum-content">
                        <ManageCurriculums />
                    </div>
                );
            case "evaluation-center":
                return (
                    <div className="evaluation-content">
                        <AdminEvaluationCenter userType="super_admin" userData={{ _id: userId }} />
                    </div>
                );
            case "my-account":
                return (
                    <div className="account-content">
                        <ModifyUser userId={userId} userType="admin" isAdmin={true} />
                    </div>
                );
            case "change-password":
                return <ChangePassword />;
            case "dashboard":
            default:
                return renderDashboard();
        }
    };

    if (error) {
        return (
            <div className="page-container">
                <div className="content-box">
                    <div className="error-container">
                        <div className="error-icon">⚠️</div>
                        <p className="error-message">{error}</p>
                        <button 
                            className="btn btn-primary"
                            onClick={loadDashboardData}
                        >
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="page-container">
                <div className="content-box">
                    <LoadingSpinner text="Loading super admin dashboard..." variant="page" />
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <CollapsibleNavbar 
                setCurrentView={handleViewChange} 
                userType="super_admin"
                isSuperAdmin={true}
            />
            {SELF_SHELLED_VIEWS.has(currentView) ? (
                renderContent()
            ) : (
                <div className="content-box">
                    <div className="dashboard-container">
                        {renderContent()}
                    </div>
                    
                    {/* Modals */}
                    {showCreateModal && (
                    <LicenseModal
                        onSubmit={handleCreateLicense}
                        onClose={() => setShowCreateModal(false)}
                    />
                )}
                
                {showEditModal && selectedLicense && (
                    <LicenseModal
                        isEdit={true}
                        license={selectedLicense}
                        onSubmit={handleUpdateLicense}
                        onClose={() => {
                            setShowEditModal(false);
                            setSelectedLicense(null);
                        }}
                    />
                )}
                
                    {selectedLicense && !showEditModal && (
                        <LicenseDetailModal
                            license={selectedLicense}
                            onClose={() => setSelectedLicense(null)}
                            onEdit={(license) => {
                                setShowEditModal(true);
                        }}
                        onDelete={handleDeleteLicense}
                    />
                )}

                {/* New Enhanced Modals */}
                {showExtendModal && selectedLicense && (
                    <LicenseExtensionModal
                        license={selectedLicense}
                        onExtend={handleExtendLicense}
                        onClose={() => {
                            setShowExtendModal(false);
                            setSelectedLicense(null);
                        }}
                    />
                )}

                {showExpireModal && selectedLicense && (
                    <LicenseExpirationModal
                        license={selectedLicense}
                        onExpire={handleExpireLicense}
                        onClose={() => {
                            setShowExpireModal(false);
                            setSelectedLicense(null);
                            }}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

// License Extension Modal Component
const LicenseExtensionModal = ({ license, onExtend, onClose }) => {
    const [days, setDays] = useState(30);
    const [customDays, setCustomDays] = useState('');
    const [useCustom, setUseCustom] = useState(false);

    if (!license) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        const extensionDays = useCustom ? parseInt(customDays) || 30 : days;
        onExtend(license._id, extensionDays);
    };

    const calculateNewEndDate = () => {
        const currentEndDate = new Date(license.licenseEndDate);
        const extensionDays = useCustom ? parseInt(customDays) || 30 : days;
        const newEndDate = new Date(currentEndDate);
        newEndDate.setDate(newEndDate.getDate() + extensionDays);
        return newEndDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content extend-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>📅 Extend License Duration</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                
                <div className="extend-content">
                    <div className="license-info">
                        <h3>{license.organizationName}</h3>
                        <p><strong>Current End Date:</strong> {new Date(license.licenseEndDate).toLocaleDateString()}</p>
                        <p><strong>Status:</strong> <span className={`status-badge status-${license.licenseStatus}`}>{license.licenseStatus}</span></p>
                    </div>
                    
                    <form onSubmit={handleSubmit} className="extend-form">
                        <div className="extension-options">
                            <h4>Extension Duration:</h4>
                            
                            <div className="quick-options">
                                {[30, 60, 90, 180, 365].map(option => (
                                    <button
                                        key={option}
                                        type="button"
                                        className={`quick-option ${!useCustom && days === option ? 'selected' : ''}`}
                                        onClick={() => {
                                            setDays(option);
                                            setUseCustom(false);
                                        }}
                                    >
                                        {option} days
                                    </button>
                                ))}
                            </div>
                            
                            <div className="custom-option">
                                <label className="custom-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={useCustom}
                                        onChange={(e) => setUseCustom(e.target.checked)}
                                    />
                                    Custom duration
                                </label>
                                {useCustom && (
                                    <input
                                        type="number"
                                        value={customDays}
                                        onChange={(e) => setCustomDays(e.target.value)}
                                        placeholder="Enter days"
                                        min="1"
                                        max="3650"
                                        className="custom-input"
                                    />
                                )}
                            </div>
                        </div>
                        
                        <div className="new-end-date">
                            <strong>New End Date:</strong> {calculateNewEndDate()}
                        </div>
                        
                        <div className="modal-actions">
                            <button type="button" className="btn btn-outline" onClick={onClose}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-success">
                                <span className="btn-icon">📅</span>
                                Extend License
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

// License Expiration Confirmation Modal Component
const LicenseExpirationModal = ({ license, onExpire, onClose }) => {
    if (!license) return null;

    const handleConfirm = () => {
        onExpire(license._id);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content expire-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header warning">
                    <h2>⚠️ Expire License</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                
                <div className="expire-content">
                    <div className="warning-message">
                        <div className="warning-icon">⚠️</div>
                        <div className="warning-text">
                            <h3>Are you sure you want to expire this license?</h3>
                            <p>This action will make the license inactive and stop all services for this organization.</p>
                        </div>
                    </div>
                    
                    <div className="license-details">
                        <h4>{license.organizationName}</h4>
                        <div className="detail-grid">
                            <div className="detail-item">
                                <span>License Key:</span>
                                <span>{license.licenseKey}</span>
                            </div>
                            <div className="detail-item">
                                <span>Current Status:</span>
                                <span className={`status-badge status-${license.licenseStatus}`}>{license.licenseStatus}</span>
                            </div>
                            <div className="detail-item">
                                <span>End Date:</span>
                                <span>{new Date(license.licenseEndDate).toLocaleDateString()}</span>
                            </div>
                            <div className="detail-item">
                                <span>Affected Users:</span>
                                <span>{license.maxStudents + license.maxPreceptors + license.maxStandaloneAdmins} users</span>
                            </div>
                        </div>
                    </div>
                    
                    <div className="consequences">
                        <h4>⚠️ Consequences:</h4>
                        <ul>
                            <li>All users will lose access to the system</li>
                            <li>Active schedules and data will be preserved but inaccessible</li>
                            <li>The license status will change to "expired"</li>
                            <li>This action can be reversed by extending the license</li>
                        </ul>
                    </div>
                    
                    <div className="modal-actions">
                        <button className="btn btn-outline" onClick={onClose}>
                            Cancel
                        </button>
                        <button className="btn btn-warning" onClick={handleConfirm}>
                            <span className="btn-icon">⏰</span>
                            Expire License
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SuperAdminDashboard;
